import React, { FC, FormEventHandler, ReactElement } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, TextField, Stack, Typography } from "@components/base";

export type LoginFormValues = {
  email: string;
  password: string;
  isOtpRequired: boolean;
  otp?: string;
};

export interface LoginForm {
  onSubmit: FormEventHandler<HTMLFormElement>;
  control: Control<LoginFormValues>;
  submitting: boolean;
  isValid: boolean;
  isOtpRequired: boolean;
  errorMessage?: string;
}

const LoginForm: FC<LoginForm> = ({
  onSubmit,
  submitting,
  control,
  isValid,
  isOtpRequired,
  errorMessage,
}) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit}>
      <Stack direction="column" rowGap={3}>
        <>
          {isOtpRequired ? (
            <Controller
              control={control}
              name="otp"
              render={({
                field: { onBlur, onChange, name, value },
                fieldState: { error },
              }): ReactElement => (
                <TextField
                  {...{ onBlur, onChange, name, value }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("otp.code.title")}
                  type="text"
                  disabled={submitting}
                />
              )}
            />
          ) : (
            <>
              <Controller
                control={control}
                name="email"
                render={({
                  field: { onBlur, onChange, name, value },
                  fieldState: { error },
                }): ReactElement => (
                  <TextField
                    {...{ onBlur, onChange, name, value }}
                    error={Boolean(error)}
                    helperText={error?.message}
                    label={t("field.title.emailAddress")}
                    type="email"
                    disabled={submitting}
                  />
                )}
              />
              <Controller
                control={control}
                name="password"
                render={({
                  field: { onBlur, onChange, name, value },
                  fieldState: { error },
                }): ReactElement => (
                  <TextField
                    {...{ onBlur, onChange, name, value }}
                    error={Boolean(error)}
                    helperText={error?.message}
                    label={t("field.title.password")}
                    type="password"
                    disabled={submitting}
                  />
                )}
              />
            </>
          )}
          {errorMessage && (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          )}
        </>

        <Button
          disabled={submitting || !isValid}
          size="medium"
          type="submit"
          variant="contained"
          isLoading={submitting}
        >
          {t("auth.login")}
        </Button>
      </Stack>
    </form>
  );
};

export default LoginForm;
